/**
 * TrajetBuilder - Ordered stop list for building trajets in a mission
 */
import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, MapPin, ArrowDown } from 'lucide-react';

export default function TrajetBuilder({ trajets = [], onChange, readOnly = false }) {
  const [dragIndex, setDragIndex] = useState(null);

  const addTrajet = () => {
    const newTrajet = {
      _tempId: Date.now(),
      from_label: '',
      to_label: '',
      distance_planned_km: '',
      purpose: '',
      planned_departure: '',
      planned_arrival: '',
    };
    onChange([...trajets, newTrajet]);
  };

  const updateTrajet = (index, field, value) => {
    const updated = [...trajets];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeTrajet = (index) => {
    onChange(trajets.filter((_, i) => i !== index));
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;

    const reordered = [...trajets];
    const [removed] = reordered.splice(dragIndex, 1);
    reordered.splice(index, 0, removed);
    onChange(reordered);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">
          Trajets ({trajets.length})
        </h4>
        {!readOnly && (
          <button
            type="button"
            onClick={addTrajet}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
          >
            <Plus className="h-4 w-4" />
            Ajouter un trajet
          </button>
        )}
      </div>

      {trajets.length === 0 && (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
          <MapPin className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Ajoutez des étapes pour cette mission</p>
        </div>
      )}

      {trajets.map((trajet, idx) => (
        <div
          key={trajet.id || trajet._tempId || idx}
          draggable={!readOnly}
          onDragStart={() => handleDragStart(idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDragEnd={handleDragEnd}
          className={`border rounded-lg p-3 bg-white ${
            dragIndex === idx ? 'border-blue-400 shadow-md' : 'border-gray-200'
          } ${!readOnly ? 'cursor-grab active:cursor-grabbing' : ''}`}
        >
          <div className="flex items-start gap-2">
            {!readOnly && (
              <GripVertical className="h-5 w-5 text-gray-300 mt-1 flex-shrink-0" />
            )}
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center flex-shrink-0 mt-1">
              {idx + 1}
            </span>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Départ</label>
                <input
                  type="text"
                  value={trajet.from_label || ''}
                  onChange={(e) => updateTrajet(idx, 'from_label', e.target.value)}
                  readOnly={readOnly}
                  placeholder="Adresse ou nom du lieu"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Arrivée</label>
                <input
                  type="text"
                  value={trajet.to_label || ''}
                  onChange={(e) => updateTrajet(idx, 'to_label', e.target.value)}
                  readOnly={readOnly}
                  placeholder="Adresse ou nom du lieu"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Distance estimée (km)</label>
                <input
                  type="number"
                  value={trajet.distance_planned_km || ''}
                  onChange={(e) => updateTrajet(idx, 'distance_planned_km', e.target.value)}
                  readOnly={readOnly}
                  placeholder="0"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Motif</label>
                <input
                  type="text"
                  value={trajet.purpose || ''}
                  onChange={(e) => updateTrajet(idx, 'purpose', e.target.value)}
                  readOnly={readOnly}
                  placeholder="Livraison, collecte..."
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                />
              </div>
            </div>

            {!readOnly && (
              <button
                type="button"
                onClick={() => removeTrajet(idx)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                title="Supprimer ce trajet"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
